import React, { useEffect, useMemo, useState } from 'react';
import styles from '../../styles/GestionInteresadosStyles/GestionInteresados.module.css';
import {
  createSeguimientoInteresado,
  getSeguimientosInteresado,
  updateSeguimientoInteresado,
  actualizarInteresado,
} from '../../api/interesados';
import { getUsuarios } from '../../api/usuarios';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function FollowUpTab({ prospect, onUpdateEstado, onRefresh }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);

  const [newComment, setNewComment] = useState('');
  const [editingId, setEditingId] = useState(null);

  const [usuarios, setUsuarios] = useState([]);
  const [selectedResponsible, setSelectedResponsible] = useState(prospect.idResponsableSeguimiento || '');
  const [lastCommunicationDate, setLastCommunicationDate] = useState(prospect._raw?.fecha_ultima_comunicacion || '');

  const comentarios = useMemo(() => {
    return history.map((item) => ({
      id: item.id_seguimiento,
      tipo: item.tipo_seguimiento,
      author: item.usuario_accion_nombre || item.responsable_nombre || 'Sistema',
      text: item.comentario || item.descripcion_recordatorio || 'Sin descripción',
      date: item.fecha_creacion,
      fechaRecordatorio: item.fecha_recordatorio,
    }));
  }, [history]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [seguimientosData, usuariosData] = await Promise.all([
        getSeguimientosInteresado(prospect.id),
        getUsuarios('ADMISIONES'),
      ]);
      setHistory(Array.isArray(seguimientosData) ? seguimientosData : []);
      setUsuarios(Array.isArray(usuariosData) ? usuariosData.filter((u) => u.activo) : []);
    } catch (error) {
      console.error('[FollowUpTab] Error cargando seguimiento:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [prospect.id]);

  const saveMeta = async () => {
    try {
      setSaving(true);
      await actualizarInteresado(prospect.id, {
        responsable_seguimiento: selectedResponsible || null,
        fecha_ultima_comunicacion: lastCommunicationDate || null,
      });

      await createSeguimientoInteresado({
        id_interesado: prospect.id,
        tipo_seguimiento: 'COMENTARIO',
        comentario: 'Actualización de responsable/fecha de última comunicación',
        responsable_seguimiento: selectedResponsible || null,
        fecha_ultima_comunicacion: lastCommunicationDate || null,
      });

      await loadData();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('[FollowUpTab] Error guardando datos de seguimiento:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAddOrUpdateComment = async () => {
    if (!newComment.trim()) return;

    try {
      setSaving(true);
      if (editingId) {
        await updateSeguimientoInteresado(editingId, {
          comentario: newComment.trim(),
        });
      } else {
        await createSeguimientoInteresado({
          id_interesado: prospect.id,
          tipo_seguimiento: 'COMENTARIO',
          comentario: newComment.trim(),
          responsable_seguimiento: selectedResponsible || null,
          fecha_ultima_comunicacion: lastCommunicationDate || null,
        });

        if (onUpdateEstado && prospect.estado !== 'SEGUIMIENTO') {
          await onUpdateEstado(prospect.id, 'SEGUIMIENTO', 'Cambio automático por nueva interacción de seguimiento');
        }
      }

      setNewComment('');
      setEditingId(null);
      await loadData();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('[FollowUpTab] Error guardando comentario:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleEditComment = (comment) => {
    setEditingId(comment.id);
    setNewComment(comment.text || '');
  };

  return (
    <div className={styles.tabContent}>
      <div className={styles.followUpGrid}>
        <div className={styles.followUpLeft}>
          <div className={styles.commentsSection}>
            <label className={styles.sectionLabel}>Comentarios de seguimiento</label>
            <textarea
              className={styles.commentTextarea}
              placeholder="Escribir comentario..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              rows={4}
            />
          </div>

          <div className={styles.commentsHistory}>
            <div className={styles.historyHeader}>
              <span className={styles.historyTitle}>Historial de comentarios</span>
              <button className={styles.addCommentButton} onClick={handleAddOrUpdateComment} disabled={saving || loading}>
                {editingId ? 'Guardar edición' : 'Agregar comentario'}
              </button>
            </div>

            <div className={styles.commentsList}>
              {loading && <div className={styles.commentText}>Cargando historial...</div>}
              {!loading && comentarios.length === 0 && <div className={styles.commentText}>Sin registros de seguimiento.</div>}
              {!loading && comentarios.map((comment) => (
                <div key={comment.id} className={styles.commentItem}>
                  <div className={styles.commentHeader}>
                    <span className={styles.commentAuthor}>{comment.author}</span>
                    <span className={styles.commentDate}>{formatDate(comment.date)}</span>
                  </div>
                  <p className={styles.commentText}>{comment.text}</p>
                  {comment.tipo === 'RECORDATORIO' && comment.fechaRecordatorio && (
                    <p className={styles.commentDate}>Recordatorio: {comment.fechaRecordatorio}</p>
                  )}
                  {comment.tipo !== 'CAMBIO_ESTADO' && (
                    <button
                      type="button"
                      className={styles.addCommentButton}
                      style={{ marginTop: '8px', padding: '6px 10px' }}
                      onClick={() => handleEditComment(comment)}
                    >
                      Editar
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.followUpRight}>
          <div className={styles.responsibleSection}>
            <label className={styles.fieldLabel}>Responsable de seguimiento</label>
            <select
              className={styles.selectField}
              value={selectedResponsible}
              onChange={(e) => setSelectedResponsible(e.target.value)}
            >
              <option value="">- Selecciona un usuario -</option>
              {usuarios.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.communicationSection}>
            <label className={styles.fieldLabel}>Fecha de última comunicación</label>
            <div className={styles.dateInputContainer}>
              <input
                type="date"
                className={styles.dateInput}
                value={lastCommunicationDate || ''}
                onChange={(e) => setLastCommunicationDate(e.target.value)}
              />
              <div className={styles.calendarIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </div>
            </div>
            <button className={styles.programButton} onClick={saveMeta} disabled={saving || loading}>
              Guardar datos
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FollowUpTab;
